import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../layouts/user';
import UserHead from '../../components/user/head';
import WalletNav from '../../components/wallet/nav';
import NeedVerification from '../../components/common/needVerification';
import { post } from '../../libs/api';
import { showAmount } from '../../libs/util';
import { getUser } from '../../libs/user';
import { useCapabilityBlocked } from '../../components/common/restriction';
import { CAPABILITY } from '../../libs/restrictions';

function Page() {
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [clientId, setClientId] = useState('');
  const [depositAddress, setDepositAddress] = useState([]);
  // PB-2323: a blocked action's button is hidden, and so are the deposit addresses.
  const depositBlocked = useCapabilityBlocked(CAPABILITY.DEPOSIT);
  const withdrawBlocked = useCapabilityBlocked(CAPABILITY.WITHDRAW);

  const filterAccounts = (list) => {
    let filtered = [];
    for (const acc of list) {
      filtered.push(acc);
    }
    return filtered.reverse();
  };

  const loadAccounts = async () => {
    setLoading(true);
    const userData = await getUser();
    setClientId(userData.manage_user.clientId);

    const data = await post('/wallet/balance', {});
    if (data.status == 200) {
      setAccounts(filterAccounts(data.data));
    }
    setLoading(false);
  };

  const loadDepositAddress = async () => {
    setLoading(true);
    const response = await post('/wallet/deposit/address', {});
    if (response.status == 200) {
      const filteredAddress = response.data.filter(
        (add) => add.currency !== 'USD' && add.currency !== 'SGD'
      );
      setDepositAddress(filteredAddress.reverse());
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  // PB-2319: ask for deposit addresses only when deposits are allowed. The backend refuses the
  // request under a DEPOSIT block and records it as a deposit attempt in Compliance Checks ->
  // History, so fetching them just to hide them logged a deposit on every visit to this page.
  useEffect(() => {
    if (depositBlocked) {
      setDepositAddress([]);
      return;
    }
    loadDepositAddress();
  }, [depositBlocked]);

  return (
    <Layout>
      <UserHead sub="" title="Wallet Balances" description="">
        {!depositBlocked && (
          <ul className="nk-block-tools gx-3">
            <li>
              <Link to="/portal/deposit" className="btn btn-primary">
                <span>Deposit</span>{' '}
                <em className="icon ni ni-arrow-long-right"></em>
              </Link>
            </li>
          </ul>
        )}
      </UserHead>

      <WalletNav active="balance" />

      <div className="nk-block nk-block-xs">
        {!loading && !verified && <NeedVerification />}

        {!loading && verified && (
          <>
            <div className="nk-block-head nk-block-head-sm">
              <div className="nk-block-between">
                <div className="nk-block-head-content">
                  <h6 className="nk-block-title">Available Coins</h6>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <table className="table table-ulogs">
                <thead className="thead-light">
                  <tr>
                    <th className="tb-col-os">
                      <span className="overline-title">Coin</span>
                    </th>
                    <th className="tb-col-ip">
                      <span className="overline-title">Total</span>
                    </th>
                    <th className="tb-col-time">
                      <span className="overline-title">Available</span>
                    </th>
                    <th className="tb-col-time">
                      <span className="overline-title">Hold</span>
                    </th>
                    <th className="tb-col-time">
                      <span className="overline-title">In Order</span>
                    </th>
                    <th className="tb-col-action">
                      <span className="overline-title">&nbsp;</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a, i) => (
                    <tr key={i}>
                      <td className="tb-col-os">
                        <span className="sub-text">{a.currency}</span>
                      </td>
                      <td className="tb-col-ip">
                        <span className="sub-text">
                          {showAmount(
                            a.main_balance + a.hold_balance + a.invest_balance,
                            a.currency,
                            null,
                            true
                          )}
                        </span>
                      </td>
                      <td className="tb-col-ip">
                        <span className="sub-text">
                          {showAmount(a.main_balance, a.currency, null, true)}
                        </span>
                      </td>
                      <td className="tb-col-ip">
                        <span className="sub-text">
                          {showAmount(a.hold_balance, a.currency, null, true)}
                        </span>
                      </td>
                      <td className="tb-col-ip">
                        <span className="sub-text">
                          {showAmount(a.invest_balance, a.currency, null, true)}
                        </span>
                      </td>
                      <td className="tb-col-action">
                        {!depositBlocked &&
                          a.currency !== 'USD' &&
                          a.currency !== 'SGD' && (
                            <Link
                              to="/portal/deposit"
                              className="btn btn-sm btn-primary"
                            >
                              Deposit
                            </Link>
                          )}
                        &nbsp;{' '}
                        {!withdrawBlocked && a.currency !== 'USD' && (
                          <Link
                            to="/portal/withdraw"
                            className="btn btn-sm btn-primary"
                          >
                            Withdraw
                          </Link>
                        )}
                        {/*&nbsp; <Link to={"/portal/swap/"+a.currency} className="btn btn-sm btn-primary">Swap</Link>*/}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {
          // First Fragment open
          !depositBlocked && depositAddress.length > 0 && (
            <>
              <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                  <div className="nk-block-head-content">
                    <h6 className="nk-block-title">Deposit Address</h6>
                  </div>
                </div>
              </div>
              <div className="card card-bordered">
                <table className="table table-ulogs">
                  <thead className="thead-light">
                    <tr>
                      <th className="tb-col-os">
                        <span className="overline-title">Coin</span>
                      </th>
                      <th className="tb-col-ip">
                        <span className="overline-title">Address</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {depositAddress.map((address, i) => (
                      <tr key={i}>
                        <td className="tb-col-os">
                          <span className="sub-text">{address.currency}</span>
                        </td>
                        <td className="tb-col-ip">
                          <span className="sub-text">{address.address}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </> //the Fragment Close here
          )
        }
      </div>
    </Layout>
  );
}

export default Page;
